import { deepCopy } from 'commons/utils';
import store from 'commons/store/Store';
import compositionCategoryHelper from 'commons/composition/compositionCategoryHelper';
import {
  Composition,
  TranslationValue,
  PublishingCategoryConfig,
  Sellable,
  RestrictedAppearanceGroup,
  getCategories,
  hasApparelCategory,
} from '@spreadgroup/common-partner-lib/composition';
import { ExtendedApparelTargetGroupLabel } from '@spreadgroup/common-partner-lib/publishing';
import { getCombinedDesignsImageUrl } from 'commons/image/imageUrlBuilder.ts';

export default {
  getMetaDataPart({
    composition,
    part,
    language,
  }: {
    composition: Composition;
    part: 'name' | 'description' | 'tags';
    language?: string;
  }) {
    const metaData = composition.metaData[part] || [];

    return metaData.find(
      (value: TranslationValue) =>
        value.language ===
        (language || composition.metaData.originLanguageCode),
    );
  },
  getDefaultMetaData(composition: Composition) {
    return {
      name: this.getMetaDataPart({ composition, part: 'name' }),
      description: this.getMetaDataPart({ composition, part: 'description' }),
      tags: this.getMetaDataPart({ composition, part: 'tags' }),
    };
  },
  addEmptyDataStructureForDefaultMetaData(composition: Composition) {
    const comp = deepCopy(composition);

    if (
      !comp.metaData.name?.find(
        (v: TranslationValue) =>
          v.language === composition.metaData.originLanguageCode,
      )
    ) {
      comp.metaData.name = [
        ...(comp.metaData.name || []),
        {
          language: comp.metaData.originLanguageCode,
          value: '',
        },
      ];
    }
    if (
      !comp.metaData.description?.find(
        (v: TranslationValue) =>
          v.language === composition.metaData.originLanguageCode,
      )
    ) {
      comp.metaData.description = [
        ...(comp.metaData.description || []),
        {
          language: comp.metaData.originLanguageCode,
          value: '',
        },
      ];
    }
    if (
      !comp.metaData.tags?.find(
        (v: TranslationValue) =>
          v.language === composition.metaData.originLanguageCode,
      )
    ) {
      comp.metaData.tags = [
        ...(comp.metaData.tags || []),
        {
          language: comp.metaData.originLanguageCode,
          values: [],
        },
      ];
    }

    return comp;
  },
  resetMetaData(composition: Composition) {
    const comp = deepCopy(composition);

    delete comp.metaData.name;
    delete comp.metaData.description;
    delete comp.metaData.tags;

    return this.addEmptyDataStructureForDefaultMetaData(comp);
  },
  getDesignImage(
    design: number,
    { height = 300, width = 300 }: { height?: number; width?: number } = {},
  ) {
    return `${store.state.publishingCore.config?.image?.templates?.design?.replace(
      '$DESIGN',
      design,
    )},width=${width},height=${height}`;
  },
  getMainDesignImage(
    composition: Composition,
    { height = 300, width = 300 }: { height?: number; width?: number } = {},
  ) {
    if (composition.designs?.length > 1) {
      return getCombinedDesignsImageUrl(
        composition.designs.map((d) => d.id),
        { height, width },
      );
    }

    return this.getDesignImage(composition.mainDesign?.id, { height, width });
  },
  getProductImage(vpString: string, viewId: number) {
    return store.state.publishingCore.config?.image?.templates?.product
      ?.replace('$VPSTRING', vpString)
      ?.replace('$VIEW', viewId);
  },
  getDefaultActivatedSellableImages(composition: Composition): string[][] {
    return composition.sellables.map((sellable: Sellable) => [
      this.getProductImage(sellable.vpString, sellable.defaultViewId),
    ]);
  },
  getAllActivatedSellableImages(composition: Composition): string[][] {
    return composition.sellables.map(
      (sellable: Sellable) =>
        sellable.printViewIds?.map((viewId: number) =>
          this.getProductImage(sellable.vpString, viewId),
        ) || [],
    );
  },
  getDefaultHotspot(composition: Composition) {
    return composition.defaultValues.defaultHotspot;
  },
  getHotspotConfigs(composition: Composition) {
    return composition.templateProducts?.[0]?.templateProduct.hotspotConfigs;
  },
  getDefaultAppearanceGroup(composition: Composition) {
    const appearanceGroupTranslations =
      store.state.publishingCore.translations.appearanceGroups;

    const defaultAppearanceGroup =
      composition.defaultValues.defaultAppearanceGroup;
    return !defaultAppearanceGroup
      ? null
      : {
          ...defaultAppearanceGroup,
          name: appearanceGroupTranslations
            ? appearanceGroupTranslations[defaultAppearanceGroup.id]?.name
            : null,
        };
  },
  getDisabledAppearanceGroupIds(composition: Composition) {
    return (
      composition.defaultValues?.restrictedAppearanceGroups?.map(
        (appearanceGroup: RestrictedAppearanceGroup) => appearanceGroup.id,
      ) || []
    );
  },
  getCategories(composition: Composition) {
    const mainCategoriesList = Object.values(
      store.state.publishingCore.mainCategories,
    ) as PublishingCategoryConfig[];
    return getCategories(composition, mainCategoriesList);
  },
  hasApparelCategory(composition: Composition) {
    return hasApparelCategory(this.getCategories(composition));
  },
  transformCategoryListToAssortmentRule({
    categories,
    labelsForCategory,
  }: {
    categories: PublishingCategoryConfig[];
    labelsForCategory: Record<string, ExtendedApparelTargetGroupLabel[]>;
  }) {
    const mainCategoryCount = Object.values(
      store.state.publishingCore.mainCategories,
    ).length;
    const labelCount = Object.values(
      store.state.publishingCore.apparelTargetGroupLabels,
    ).length;

    const assortmentRule = {
      categories: categories.map((category) => ({
        categoryId: category.id,
        labelTerm:
          labelsForCategory &&
          labelsForCategory[category.id] &&
          labelsForCategory[category.id].length < labelCount // in case all labels are attached no labelTerm is needed
            ? {
                labels: labelsForCategory[category.id].map((label) => ({
                  labelId: label.id,
                  labelGroup: 'MP_CLOTHING_DEPARTMENTS', // todo: refactor hardcoded label group
                })),
                operator: 'OR',
              }
            : undefined,
      })),
    };

    // in case all categories are included and no category is limited by labels we dont need the assortment rule
    if (
      assortmentRule.categories.length === mainCategoryCount &&
      !assortmentRule.categories.some((category) => !!category.labelTerm)
    ) {
      return {};
    } else {
      return assortmentRule;
    }
  },
  hasMultipleDesigns: (composition?: Composition): boolean =>
    !!(composition?.designs && composition?.designs?.length > 1),
};
